import React, { useState } from "react";
import { Alert, Button as SetlistButton, Plus } from "@opendate-app/setlist";
import BreadcrumbChevron from "../../shared/components/BreadcrumbChevron";
import CollectionsNavIcon from "../../shared/Icons/CollectionsNavIcon";
import SeriesNavIcon from "../../shared/Icons/SeriesNavIcon";
import ContainerlessTableViewApp from "../../TableView/startup/ContainerlessTableViewApp";
import TeamSearchBar from "../../TeamManager/components/TeamSearchBar";
import TabButton from "./TabButton";
import NewSeriesModal from "./NewSeriesModal";
import { useSeriesIndexContext } from "../SeriesIndexApp";

const COLLECTIONS_ALERT_KEY = "seriesIndex.collectionsAlertDismissed";

const readCollectionsAlertDismissed = () => {
  try {
    return window.localStorage.getItem(COLLECTIONS_ALERT_KEY) === "true";
  } catch (e) {
    return false;
  }
};

const storeCollectionsAlertDismissed = () => {
  try {
    window.localStorage.setItem(COLLECTIONS_ALERT_KEY, "true");
  } catch (e) {
    return;
  }
};

const SeriesIndex = () => {
  const {
    teamId,
    activeCount,
    archivedCount,
    draftCount,
    setupWizardEnabled,
    activeTab,
    searchTerm,
    setSearchTerm,
    dataSource,
    columns,
    rowCallback,
    canCreateSeries,
    venueOwnerships,
    createDraft,
    creatingDraft,
    collectionsPath,
  } = useSeriesIndexContext();

  const [newSeriesOpen, setNewSeriesOpen] = useState(false);
  const [collectionsAlertDismissed, setCollectionsAlertDismissed] = useState(readCollectionsAlertDismissed);
  const dismissCollectionsAlert = () => {
    storeCollectionsAlertDismissed();
    setCollectionsAlertDismissed(true);
  };
  const startNewSeries = () => (setupWizardEnabled ? createDraft() : setNewSeriesOpen(true));

  const isArchived = activeTab === "archived";
  const isDraft = activeTab === "draft";
  const showDraftsTab = setupWizardEnabled || draftCount > 0;
  const currentTabCount = isDraft ? draftCount : isArchived ? archivedCount : activeCount;

  const emptyState = {
    draft: ["No drafts", "A series you start but don't publish will wait for you here."],
    archived: ["No archived series", "Series you archive will show up here."],
    active: [
      "No series yet",
      "Group related events into a recurring series to manage them together.",
    ],
  }[activeTab];

  return (
    <div className="series-index-page">
      <nav aria-label="breadcrumb" className="mb-3">
        <div className="d-flex align-items-center flex-wrap series-index-breadcrumb">
          <a href="/dashboard" className="settings-breadcrumb-link" aria-label="Home">
            <i className="fal fa-home" />
          </a>
          <BreadcrumbChevron />
          <a href="/dashboard" className="settings-breadcrumb-link">Calendar</a>
          <BreadcrumbChevron />
          <span className="settings-breadcrumb-current">Series</span>
        </div>
      </nav>

      <div className="mb-3 d-flex align-items-start justify-content-between series-index-title-row">
        <div>
          <h1 className="series-index-title">
            Series
          </h1>
          <p className="series-index-subtitle">
            All your recurring event series, in one place.
          </p>
        </div>
        {canCreateSeries && (
          <SetlistButton
            variant="primary"
            className="series-index-new-btn"
            icon={Plus}
            onClick={startNewSeries}
            disabled={creatingDraft}
          >
            New series
          </SetlistButton>
        )}
      </div>

      {setupWizardEnabled && !collectionsAlertDismissed && (
        <div className="mb-3">
          <Alert
            color="brand"
            icon={CollectionsNavIcon}
            title="Collections have their own page now"
            description="Series is just for recurring events. Your festivals and takeovers moved to Tickets › Collections, with all their shows, passes, and links intact."
            onDismiss={dismissCollectionsAlert}
            actions={(
              <>
                <SetlistButton variant="link-gray" size="sm" onClick={dismissCollectionsAlert}>Dismiss</SetlistButton>
                <SetlistButton variant="link-color" size="sm" as="a" href={collectionsPath}>Go to Collections</SetlistButton>
              </>
            )}
          />
        </div>
      )}

      {canCreateSeries && !setupWizardEnabled && (
        <NewSeriesModal
          open={newSeriesOpen}
          onClose={() => setNewSeriesOpen(false)}
          teamId={teamId}
          venueOwnerships={venueOwnerships}
        />
      )}

      <div className="d-inline-flex series-index-tabs">
        <TabButton label="Active" tab="active" />
        {showDraftsTab && <TabButton label="Drafts" tab="draft" />}
        <TabButton label="Archived" tab="archived" />
      </div>

      {currentTabCount === 0 ? (
        <div className="team-member-card">
          <div className="team-empty-state">
            <div className="team-empty-state-icon">
              <SeriesNavIcon className="" size={48} />
            </div>
            <div className="team-empty-state-title">{emptyState[0]}</div>
            <div className="team-empty-state-text">{emptyState[1]}</div>
            {!isArchived && !isDraft && canCreateSeries && (
              <div className="team-empty-state-actions">
                <SetlistButton
                  variant="primary"
                  className="series-index-new-btn"
                  icon={Plus}
                  onClick={startNewSeries}
                  disabled={creatingDraft}
                >
                  New series
                </SetlistButton>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="team-member-card">
          <div className="team-search-filters-row">
            <div className="team-search-filters-left">
              <TeamSearchBar
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                placeholder="Search series..."
              />
            </div>
          </div>
          <ContainerlessTableViewApp
            key={`series-${activeTab}-${searchTerm}`}
            dataSource={dataSource}
            columns={columns}
            order={[[3, activeTab === "archived" ? "desc" : "asc"]]}
            rowCallback={rowCallback}
          />
        </div>
      )}
    </div>
  );
};

export default SeriesIndex;
